import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from '@mui/material';
import { appointmentsAPI } from '../services/api';
import { formatDate, formatDateTime } from '../utils/dateFormat';
import { toArray } from '../utils/apiData';
import { CardSkeleton } from './LoadingSkeletons';

const formatDateTimeDisplay = (value) => formatDateTime(value) ?? '-';
const formatDateDisplay = (value) => formatDate(value) ?? '-';

const formatCurrencyDisplay = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const number = typeof value === 'number' ? value : parseFloat(value);
  if (Number.isNaN(number)) return '-';
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
  }).format(number);
};

const getStatusColor = (status) => {
  switch (status) {
    case 'completed':
      return 'success';
    case 'scheduled':
      return 'primary';
    case 'cancelled':
      return 'error';
    case 'no_show':
      return 'warning';
    default:
      return 'default';
  }
};

const HorseVetRecords = ({ horseId }) => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadVetRecords = useCallback(async () => {
    try {
      setLoading(true);
      const response = await appointmentsAPI.getVetAppointments({ horse_id: horseId });
      
      // Sort by appointment date (most recent first)
      const sortedAppointments = toArray(response.data, 'vet records').sort((a, b) => {
        const dateA = new Date(a.appointment_date);
        const dateB = new Date(b.appointment_date);
        return dateB - dateA;
      });
      
      setAppointments(sortedAppointments);
    } catch (error) {
      console.error('Error loading vet records:', error);
    } finally {
      setLoading(false);
    }
  }, [horseId]);

  useEffect(() => {
    loadVetRecords();
  }, [loadVetRecords]);

  if (loading) {
    return <CardSkeleton label="Loading vet records…" />;
  }

  return (
    <Card>
      <CardContent>
        {appointments.length === 0 ? (
          <Typography color="textSecondary">No veterinary records found</Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Veterinarian</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Clinical Details</TableCell>
                  <TableCell>Follow-Up</TableCell>
                  <TableCell align="right">Billing</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {appointments.map((apt) => (
                  <TableRow key={apt.id}>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDateTimeDisplay(apt.appointment_date)}
                      </Typography>
                      {apt.completed_date && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Completed: {formatDateTimeDisplay(apt.completed_date)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {apt.appointment_type || apt.appointment_type_name || '-'}
                      </Typography>
                      {(apt.appointment_type_description || apt.appointment_type_name) && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {apt.appointment_type_description || apt.appointment_type_name}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {apt.vet_name || '-'}
                      </Typography>
                      {apt.vet_clinic && (
                      <Typography variant="caption" display="block" color="text.secondary">
                          {apt.vet_clinic}
                        </Typography>
                      )}
                      {apt.vet_phone && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {apt.vet_phone}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={apt.status || 'Unknown'}
                        size="small"
                        color={getStatusColor(apt.status)}
                      />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'pre-line', minWidth: 220 }}>
                      {apt.diagnosis && (
                        <Typography variant="body2">
                          Diagnosis: {apt.diagnosis}
                        </Typography>
                      )}
                      {apt.treatment && (
                        <Typography variant="body2" sx={{ mt: apt.diagnosis ? 0.5 : 0 }}>
                          Treatment: {apt.treatment}
                        </Typography>
                      )}
                      {apt.medications && (
                        <Typography variant="body2" sx={{ mt: (apt.diagnosis || apt.treatment) ? 0.5 : 0 }}>
                          Medications: {apt.medications}
                        </Typography>
                      )}
                      {apt.notes && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: (apt.diagnosis || apt.treatment || apt.medications) ? 0.5 : 0 }}>
                          Notes: {apt.notes}
                        </Typography>
                      )}
                      {!apt.diagnosis && !apt.treatment && !apt.medications && !apt.notes && (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={Boolean(apt.follow_up_required) ? 'Required' : 'Not Required'}
                        size="small"
                        color={Boolean(apt.follow_up_required) ? 'warning' : 'default'}
                        variant={Boolean(apt.follow_up_required) ? 'filled' : 'outlined'}
                      />
                      {apt.follow_up_date && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          Due: {formatDateDisplay(apt.follow_up_date)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">{formatCurrencyDisplay(apt.cost)}</Typography>
                      {Boolean(apt.paid) && (
                        <Chip
                          label="Paid"
                          size="small"
                          color="success"
                          variant="outlined"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default HorseVetRecords;


